import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Form, FormField } from '../../components/Form/Form';
import { Project, TimeEntry } from '../../types/api';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import * as timeEntriesAPI from '../../services/timeEntriesAPI';
import * as projectsAPI from '../../services/projectsAPI';
import { differenceInHours, differenceInMinutes, format } from 'date-fns';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box, Card, CardContent, Typography, TextField, Autocomplete, Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { TimeClock } from '../../components/TimeClock/TimeClock';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';

// Helper function to calculate total work hours
const calculateWorkHours = (startTime: Date, endTime: Date, breakDuration: number): number => {
  const totalMinutes = differenceInMinutes(endTime, startTime) - breakDuration;
  return totalMinutes / 60;
};

const validationSchema = yup.object({
  project_id: yup.number().required('Projekt ist erforderlich'),
  date: yup.date().required('Datum ist erforderlich'),
  start_time: yup.date().required('Startzeit ist erforderlich'),
  end_time: yup
    .date()
    .required('Endzeit ist erforderlich')
    .min(yup.ref('start_time'), 'Endzeit muss nach der Startzeit liegen')
    .test('max-work-time', 'Die maximale Arbeitszeit von 12 Stunden wurde überschritten', function(endTime) {
      const { start_time, break_duration } = this.parent;
      if (!endTime || !start_time || break_duration === undefined) return true;
      
      const workHours = calculateWorkHours(start_time, endTime, break_duration);
      return workHours <= 12;
    }),
  break_duration: yup
    .number()
    .min(0, 'Pausendauer muss positiv sein')
    .required('Pausendauer ist erforderlich')
    .test('min-break-time', 'Bei mehr als 6 Stunden Arbeitszeit ist eine Pause von mindestens 30 Minuten erforderlich', function(breakDuration) {
      const { start_time, end_time } = this.parent;
      if (!end_time || !start_time || breakDuration === undefined) return true;
      
      const workHours = calculateWorkHours(start_time, end_time, 0);
      return workHours <= 6 || (workHours > 6 && breakDuration >= 30);
    }),
  description: yup.string().required('Beschreibung ist erforderlich'),
});

interface FormValues {
  project_id: number;
  date: Date;
  start_time: Date;
  end_time: Date;
  break_duration: number;
  description: string;
}

export const TimeTrackingForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [timeEntry, setTimeEntry] = useState<TimeEntry | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<FormValues | null>(null);
  const theme = useTheme();

  // Rollen-basierte Berechtigungen für Zeiterfassung
  const isAdmin = user?.role === 'admin';
  const isBauleiter = user?.role === 'bauleiter';
  
  // Zeiterfassung-Berechtigungen:
  const canEditTimeEntries = isAdmin; // Nur Admin kann bearbeiten
  const canCreateTimeEntries = isAdmin || isBauleiter; // Nur Admin & Bauleiter können erstellen
  
  const isEdit = Boolean(id);
  const isView = location.pathname.includes('/view');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Berechtigung prüfen
    if (isEdit && !canEditTimeEntries) {
      enqueueSnackbar('Keine Berechtigung zum Bearbeiten von Zeiteinträgen', { variant: 'error' });
      navigate('/time-tracking');
      return;
    }
    if (!isEdit && !canCreateTimeEntries) {
      enqueueSnackbar('Keine Berechtigung zum Erstellen von Zeiteinträgen', { variant: 'error' });
      navigate('/time-tracking');
      return;
    }

    const fetchData = async () => {
      try {
        const projectsData = await projectsAPI.getProjects();
        setProjects(projectsData);

        if (id) {
          const entry = await timeEntriesAPI.getEntry(parseInt(id, 10));
          setTimeEntry(entry);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        enqueueSnackbar('Fehler beim Laden der Daten', { variant: 'error' });
      }
    };

    fetchData();
  }, [id, enqueueSnackbar]);

  const submitForm = async (values: FormValues) => {
    try {
      const selectedProject = projects.find(p => p.id === values.project_id);
      if (!selectedProject) {
        throw new Error('Selected project not found');
      }

      const formattedData: Omit<TimeEntry, 'id'> = {
        date: format(values.date, 'yyyy-MM-dd'), // Use local date format instead of UTC
        project_id: values.project_id,
        project_name: selectedProject.name,
        user_id: user?.id || 0,
        user_name: user ? `${user.first_name} ${user.last_name}` : '',
        start_time: format(values.start_time, "yyyy-MM-dd'T'HH:mm:ss"), // Local time format without timezone conversion
        end_time: format(values.end_time, "yyyy-MM-dd'T'HH:mm:ss"), // Local time format without timezone conversion
        break_duration: values.break_duration,
        description: values.description,
        // GPS-Daten hinzufügen falls vorhanden
        start_latitude: gpsData?.start_latitude,
        start_longitude: gpsData?.start_longitude,
        end_latitude: gpsData?.end_latitude,
        end_longitude: gpsData?.end_longitude,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (timeEntry) {
        await timeEntriesAPI.updateEntry(timeEntry.id, formattedData);
        enqueueSnackbar('Zeiteintrag erfolgreich aktualisiert', { variant: 'success' });
      } else {
        await timeEntriesAPI.createEntry(formattedData);
        enqueueSnackbar('Zeiteintrag erfolgreich erstellt', { variant: 'success' });
      }
      navigate('/time-tracking');
    } catch (error) {
      console.error('Error submitting time entry:', error);
      enqueueSnackbar('Fehler beim Speichern des Zeiteintrags', { variant: 'error' });
    }
  };

  const handleSubmit = async (values: FormValues) => {
    // Check for 6-hour break rule
    const workHours = calculateWorkHours(values.start_time, values.end_time, 0);
    if (workHours > 6 && values.break_duration < 30) {
      setPendingSubmit(values);
      setBreakDialogOpen(true);
    } else {
      await submitForm(values);
    }
  };

  const handleBreakDialogClose = () => {
    setBreakDialogOpen(false);
    setPendingSubmit(null);
  };

  const handleBreakDialogConfirm = async () => {
    if (pendingSubmit) {
      await submitForm(pendingSubmit);
    }
    setBreakDialogOpen(false);
    setPendingSubmit(null);
  };

  // Formular-Values als State, damit sie von der Stempeluhr gesetzt werden können
  const initialStateFromClock = location.state && {
    project_id: 0,
    date: location.state.date ? new Date(location.state.date) : new Date(),
    start_time: location.state.start_time ? new Date(location.state.start_time) : new Date(),
    end_time: location.state.end_time ? new Date(location.state.end_time) : new Date(),
    break_duration: location.state.break_duration ?? 0,
    description: '',
  };
  
  // GPS-Daten aus location.state extrahieren
  const gpsData = location.state && {
    start_latitude: location.state.start_latitude,
    start_longitude: location.state.start_longitude,
    end_latitude: location.state.end_latitude,
    end_longitude: location.state.end_longitude,
  };
  const [formValues, setFormValues] = useState<FormValues>(
    timeEntry
      ? {
          project_id: timeEntry.project_id,
          date: new Date(timeEntry.date),
          start_time: new Date(timeEntry.start_time),
          end_time: new Date(timeEntry.end_time),
          break_duration: timeEntry.break_duration,
          description: timeEntry.description,
        }
      : initialStateFromClock || {
          project_id: 0,
          date: new Date(),
          start_time: new Date(),
          end_time: new Date(),
          break_duration: 0,
          description: '',
        }
  );

  useEffect(() => {
    if (timeEntry) {
      setFormValues({
        project_id: timeEntry.project_id,
        date: new Date(timeEntry.date),
        start_time: new Date(timeEntry.start_time),
        end_time: new Date(timeEntry.end_time),
        break_duration: timeEntry.break_duration,
        description: timeEntry.description,
      });
    }
  }, [timeEntry]);

  // UX: Arbeitszeit live berechnen
  const workHours = differenceInMinutes(formValues.end_time, formValues.start_time) / 60 - (formValues.break_duration || 0) / 60;
  const workHoursStr = isNaN(workHours) ? '' : `${Math.floor(workHours)}h ${(Math.round((workHours % 1) * 60)).toString().padStart(2, '0')}min`;

  const fields: FormField[] = [
    {
      name: 'project_id',
      label: 'Projekt',
      type: 'select',
      options: projects.map(project => ({
        value: project.id,
        label: project.name,
      })),
      required: true,
    },
    {
      name: 'date',
      label: 'Datum',
      type: 'date',
      required: true,
    },
    {
      name: 'start_time',
      label: 'Startzeit',
      type: 'time',
      required: true,
    },
    {
      name: 'end_time',
      label: 'Endzeit',
      type: 'time',
      required: true,
    },
    {
      name: 'break_duration',
      label: 'Pausendauer (Minuten)',
      type: 'number',
      required: true,
    },
    {
      name: 'description',
      label: 'Beschreibung',
      type: 'text',
      multiline: true,
      rows: 4,
      required: true,
    },
  ];

  return (
    <Box maxWidth={900} mx="auto" mt={isMobile ? 1 : 4} px={isMobile ? 0.5 : 0}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.4fr' }, gap: isMobile ? 1 : 3, alignItems: 'stretch' }}>
        {/* Stempeluhr links auf Desktop, oben auf Mobile */}
        <Box sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', mb: isMobile ? 1 : 0 }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <TimeClock
                onStop={(start, end, pause) => {
                  setFormValues(fv => ({
                    ...fv,
                    start_time: start,
                    end_time: end,
                    break_duration: Math.round(pause / 60),
                    date: new Date(start.getFullYear(), start.getMonth(), start.getDate()), // Use local date components instead of UTC
                  }));
                  setTimeout(() => {
                    document.getElementById('timetracking-form')?.scrollIntoView({ behavior: 'smooth' });
                  }, 200);
                }}
              />
            </CardContent>
          </Card>
        </Box>
        {/* Formular rechts auf Desktop, unten auf Mobile */}
        <Box sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', height: '100%', boxShadow: 3, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom align={isMobile ? 'center' : 'left'}>
                {timeEntry ? 'Zeiteintrag bearbeiten' : 'Neuen Zeiteintrag anlegen'}
              </Typography>
              <Box id="timetracking-form" component="form" onSubmit={e => { e.preventDefault(); handleSubmit(formValues); }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
                  {fields.map((field) => (
                    <Box key={field.name}>
                      {field.type === 'select' ? (
                        <Autocomplete
                          options={field.options ?? []}
                          getOptionLabel={option => option.label}
                          value={(field.options ?? []).find(o => o.value === formValues[field.name as keyof FormValues]) || null}
                          onChange={(_, value) => setFormValues(fv => ({ ...fv, [field.name]: value ? value.value : 0 }))}
                          renderInput={params => <TextField {...params} label={field.label} required fullWidth size={isMobile ? 'small' : 'medium'} />}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          sx={{ minWidth: 120 }}
                        />
                      ) : field.type === 'date' ? (
                        <TextField
                          label={field.label}
                          type="date"
                          value={format(formValues[field.name as keyof FormValues], 'yyyy-MM-dd')}
                          onChange={e => setFormValues(fv => ({ ...fv, [field.name]: new Date(e.target.value) }))}
                          fullWidth
                          required
                          size={isMobile ? 'small' : 'medium'}
                          InputLabelProps={{ shrink: true }}
                        />
                      ) : field.type === 'time' ? (
                        <TextField
                          label={field.label}
                          type="time"
                          value={format(formValues[field.name as keyof FormValues], 'HH:mm')}
                          onChange={e => setFormValues(fv => {
                            const [h, m] = e.target.value.split(':');
                            const d = new Date(fv[field.name as keyof FormValues]);
                            d.setHours(Number(h), Number(m));
                            return { ...fv, [field.name]: d };
                          })}
                          fullWidth
                          required
                          size={isMobile ? 'small' : 'medium'}
                          InputLabelProps={{ shrink: true }}
                        />
                      ) : field.type === 'number' ? (
                        <TextField
                          label={field.name === 'break_duration' ? 
                            <span>Pausendauer (Minuten) <InfoOutlinedIcon fontSize="small" titleAccess="Bei mehr als 6h Arbeitszeit sind 30min Pause Pflicht." /></span> : 
                            field.label
                          }
                          type="number"
                          value={formValues[field.name as keyof FormValues]}
                          onChange={e => setFormValues(fv => ({ ...fv, [field.name]: Number(e.target.value) }))}
                          fullWidth
                          required
                          size={isMobile ? 'small' : 'medium'}
                          inputProps={{ min: 0 }}
                        />
                      ) : field.type === 'text' ? (
                        <TextField
                          label={field.label}
                          value={formValues[field.name as keyof FormValues]}
                          onChange={e => setFormValues(fv => ({ ...fv, [field.name]: e.target.value }))}
                          fullWidth
                          required
                          multiline={field.multiline}
                          rows={field.rows}
                          size={isMobile ? 'small' : 'medium'}
                          sx={field.name === 'description' ? { gridColumn: '1 / -1' } : {}}
                        />
                      ) : null}
                    </Box>
                  ))}
                  {/* Arbeitszeit-Anzeige */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'center' }}>
                    <Typography variant={isMobile ? 'body1' : 'body2'} color={workHours > 12 ? 'error' : 'primary'} fontWeight={600}>
                      Arbeitszeit: {workHoursStr}
                    </Typography>
                  </Box>
                  {/* Aktionen */}
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Box
                      display="flex"
                      flexDirection={isMobile ? 'column' : 'row'}
                      gap={2}
                      mt={2}
                      position={isMobile ? 'sticky' : 'static'}
                      bottom={isMobile ? 0 : 'auto'}
                      zIndex={isMobile ? 100 : 'auto'}
                      bgcolor={isMobile ? 'background.paper' : 'transparent'}
                      p={isMobile ? 1 : 0}
                      boxShadow={isMobile ? 3 : 0}
                    >
                      <Button variant="outlined" color="secondary" onClick={() => navigate('/time-tracking')} size={isMobile ? 'large' : 'medium'} fullWidth={isMobile}>
                        Abbrechen
                      </Button>
                      <Button variant="contained" color="primary" type="submit" size={isMobile ? 'large' : 'medium'} fullWidth={isMobile}>
                        {timeEntry ? 'Aktualisieren' : 'Speichern'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
      {/* Pausenregel-Dialog bleibt */}
      <Dialog
        open={breakDialogOpen}
        onClose={handleBreakDialogClose}
        aria-labelledby="break-dialog-title"
        aria-describedby="break-dialog-description"
        TransitionProps={{ onEnter: () => setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100) }}
      >
        <DialogTitle id="break-dialog-title">
          Gesetzliche Pausenregelung
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="break-dialog-description">
            Bei einer Arbeitszeit von mehr als 6 Stunden ist eine Pause von mindestens 30 Minuten gesetzlich vorgeschrieben.
            Möchten Sie den Zeiteintrag trotzdem speichern?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBreakDialogClose} color="primary">
            Abbrechen
          </Button>
          <Button onClick={handleBreakDialogConfirm} color="primary" variant="contained">
            Trotzdem speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 